import React, { useEffect, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Box,
  Modal,
  useModal,
  ModalClose,
  ModalHeader,
  ModalFooter,
  Body2,
  Space,
  Heading5,
  Visible,
  Display,
  DisplayPosition,
  Overflow,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'
import { useDebounce } from 'react-use'

import { FlexModalOverlay, FlexPortionFooter, RecipeTile } from '@features/recipe-tile'
import type {
  CollectionSlug,
  CollectionUUID,
  MenuRecipeTileRequestParams,
  Recipe,
  RecipeUUID,
} from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { menuSearchModalOpen } from 'actions/trackingKeys'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useOfferedRecipes } from 'hooks/menu-recipes/useOfferedRecipes'
import { SuggestedRecipe } from 'routes/Menu/components/Search/SuggestedRecipe/SuggestedRecipe'
import { useBasket } from 'routes/Menu/domains/basket'
import { useMenuPreferences } from 'routes/Menu/domains/menu'
import { useFlexRecipesWeb } from 'routes/Menu/hooks/useFlexRecipesWeb'
import { useRecipeTileRefresh } from 'routes/Menu/hooks/useRecipeTileRefresh'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketDate, getBasketMenuUuid } from 'selectors/basket'

import { RelevantRecipesSkeleton } from '../LoadingSkeleton/RelevantRecipesSkeleton'
import { NoResults } from '../NoResults/NoResults'
import { PopularCategories } from '../PopularCategories/PopularCategories'
import { RecipeDetail } from '../RecipeDetail/RecipeDetail'
import { SearchCategoriesGrid } from '../SearchCategoriesGrid/SearchCategoriesGrid'
import { SearchError } from '../SearchError/SearchError'
import { SearchInput } from '../SearchInput/SearchInput'
import { FlexSearchRecipeTile } from '../SearchRecipeTile/FlexSearchRecipeTile'
import { SearchRecipeTile } from '../SearchRecipeTile/SearchRecipeTile'
import type { SearchRecipeTileTrackingData, TrackCTAClick } from '../types'
import { getCategoryImageUrl } from '../utils'
import { useMenuSearch } from './useMenuSearch'

import css from './styles.css'

type ViewRecipeDetails = {
  invocationId?: string
  menuId: string
  recipeId: RecipeUUID | null
  recipeRank?: number
}

type FlexPortionEditorRecipe = {
  recipe: Recipe
  trackingData: SearchRecipeTileTrackingData
}

type UpdateRecipePortions = (
  recipeId: RecipeUUID,
  numPortions: MenuRecipeTileRequestParams['numPortions'],
) => void

export const recipeSearchModalName = 'RecipeSearchModal'

export const RecipeSearchModal = () => {
  const { closeCurrentModal } = useModal()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [recipeDetailsId, setRecipeDetailsId] = useState<RecipeUUID | null>(null)
  const [flexPortionEditorRecipe, setFlexPortionEditorRecipe] =
    useState<FlexPortionEditorRecipe | null>(null)
  const basketDate = useSelector(getBasketDate)
  const basketMenuUuid = useSelector(getBasketMenuUuid)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { data: menuPreferences } = useMenuPreferences()
  const isFlexRecipesWebEnabled = useFlexRecipesWeb()
  const offeredRecipes = useOfferedRecipes()
  const { basketHasRecipe } = useBasketRecipes()

  useDebounce(() => setDebouncedSearchText(searchText), 500, [searchText])

  const { data, error, isLoading, retry } = useMenuSearch({
    deliveryDate: basketDate,
    menuId: basketMenuUuid,
    searchText: debouncedSearchText,
  })

  const {
    addRecipeByUuid,
    removeRecipeByUuid,
    reachedLimit,
    canAddRecipes,
    getQuantitiesForRecipeId,
    getNumPortionsForRecipeId,
    updateRecipePortionsByUuid,
  } = useBasket()
  const currentCollectionId = useCurrentCollectionId()
  const { refreshRecipeTile } = useRecipeTileRefresh({
    deliveryDate: basketDate,
    menuId: basketMenuUuid,
  })

  const cleanUpState = () => {
    setSearchText('')
    setRecipeDetailsId(null)
    setFlexPortionEditorRecipe(null)
  }

  const viewRecipeDetails = ({ invocationId, menuId, recipeId, recipeRank }: ViewRecipeDetails) => {
    setRecipeDetailsId(recipeId)
    queueTrackingEvent<{
      action: 'view_recipe_details'
      properties: {
        menu_id: string
        recipe_id: string
        search_invocation_id?: string
        search_rank?: number
      }
    }>({
      action: 'view_recipe_details',
      properties: {
        menu_id: menuId,
        recipe_id: recipeId || '',
        ...(invocationId && { search_invocation_id: invocationId }),
        ...(recipeRank && { search_rank: recipeRank }),
      },
    })
    datadogRum.addAction('open_recipe_details', {
      menu_id: menuId,
      recipe_id: recipeId || '',
      source: 'search',
    })
  }

  const viewSearch = ({ menuId }: { menuId: string }) => {
    setRecipeDetailsId(null)
    setFlexPortionEditorRecipe(null)
    queueTrackingEvent<{
      action: typeof menuSearchModalOpen
      properties: {
        menu_id: string
      }
    }>({
      action: menuSearchModalOpen,
      properties: {
        menu_id: menuId,
      },
    })
    datadogRum.addAction('open_search', { menu_id: menuId })
  }

  const closeFlexPortionEditor = () => setFlexPortionEditorRecipe(null)

  const openFlexPortionEditor = (recipe: Recipe, trackingData: SearchRecipeTileTrackingData) => {
    setFlexPortionEditorRecipe({ recipe, trackingData })
  }

  const updateSearchRecipePortions: UpdateRecipePortions = (recipeId, numPortions) => {
    updateRecipePortionsByUuid(recipeId, numPortions)
    void refreshRecipeTile(recipeId, numPortions)
  }

  const updateFlexPortionEditorRecipePortions = (updatedNumPortions: number) => {
    if (!flexPortionEditorRecipe) {
      return
    }

    updateRecipePortionsByUuid(
      flexPortionEditorRecipe.recipe.id,
      updatedNumPortions as MenuRecipeTileRequestParams['numPortions'],
    )
    void refreshRecipeTile(
      flexPortionEditorRecipe.recipe.id,
      updatedNumPortions as MenuRecipeTileRequestParams['numPortions'],
    )
    closeFlexPortionEditor()
  }

  const removeFlexPortionEditorRecipe = () => {
    if (!flexPortionEditorRecipe) {
      return
    }

    const { recipe, trackingData } = flexPortionEditorRecipe

    queueTrackingEvent<TrackCTAClick>({
      action: 'remove_recipe',
      properties: {
        search_invocation_id: trackingData.invocationId,
        search_rank: trackingData.index,
        menu_id: trackingData.menuId,
        add_type: 'search',
        event_screen: 'menu_search_screen',
        recipe_id: recipe.id,
      },
    })
    removeRecipeByUuid(recipe.id)
    closeFlexPortionEditor()
  }

  const flexPortionEditorRecipeCount = flexPortionEditorRecipe
    ? getQuantitiesForRecipeId(flexPortionEditorRecipe.recipe.id)
    : 0
  const isFlexPortionEditorOpen = Boolean(
    flexPortionEditorRecipe && flexPortionEditorRecipeCount > 0 && !recipeDetailsId,
  )

  useEffect(() => {
    if (flexPortionEditorRecipe && flexPortionEditorRecipeCount === 0) {
      closeFlexPortionEditor()
    }
  }, [flexPortionEditorRecipe, flexPortionEditorRecipeCount])

  return (
    <Modal
      className={css.RecipeSearchModal}
      name={recipeSearchModalName}
      onRequestClose={closeCurrentModal}
      onAfterClose={cleanUpState}
      overlayClassName={recipeDetailsId || isFlexPortionEditorOpen ? css.overlay : ''}
    >
      {recipeDetailsId ? (
        <RecipeDetail
          onClose={() => viewSearch({ menuId: basketMenuUuid })}
          onSelectRecipe={setRecipeDetailsId}
          recipeId={recipeDetailsId}
        />
      ) : (
        <ModalHeader>
          <Box paddingRight={[4, 12]}>
            <SearchInput onChange={setSearchText} value={searchText} />
          </Box>
          <ModalClose onClose={closeCurrentModal} />
        </ModalHeader>
      )}
      <Box
        display={recipeDetailsId ? Display.None : Display.Initial}
        overflowY={isFlexPortionEditorOpen ? Overflow.Hidden : Overflow.Scroll}
        paddingV={[4, 6]}
        paddingH={[4, 6]}
      >
        <Box paddingH={2} paddingBottom={4}>
          {(() => {
            if (error) {
              return <SearchError onRetry={retry} />
            } else if (data) {
              const { categories, recipes, invocation_id: invocationId } = data

              return (
                <Box>
                  <Box paddingBottom={6}>
                    {recipes.length > 0 ? (
                      <>
                        <Heading5>Relevant Recipes</Heading5>
                        <Body2>On this week&apos;s menu</Body2>
                        <Space size={4} />
                        {recipes.map((recipe, index) => {
                          const trackingData = {
                            invocationId,
                            menuId: basketMenuUuid,
                            index,
                          }

                          const offeredRecipe: Recipe | null = offeredRecipes[recipe.id] ?? null
                          const recipeQuantity = getQuantitiesForRecipeId(recipe.id)
                          const recipeNumPortions = getNumPortionsForRecipeId(recipe.id)

                          const searchTrackingData = {
                            search_invocation_id: invocationId,
                            search_rank: index,
                            menu_id: basketMenuUuid,
                            add_type: 'search',
                            event_screen: 'menu_search_screen',
                            recipe_id: recipe.id,
                          }

                          return (
                            <React.Fragment key={recipe.id}>
                              <Visible display={[Display.None, Display.Block]}>
                                {offeredRecipe && (
                                  <Box paddingBottom={2}>
                                    {isFlexRecipesWebEnabled ? (
                                      <FlexSearchRecipeTile
                                        addRecipe={(recipeId) =>
                                          addRecipeByUuid(recipeId, 'menu_search_screen')
                                        }
                                        menuPreferences={menuPreferences}
                                        showCustomisedTileFooter={isAuthenticated}
                                        onClick={() =>
                                          viewRecipeDetails({
                                            invocationId,
                                            menuId: basketMenuUuid,
                                            recipeId: offeredRecipe?.id || null,
                                            recipeRank: index,
                                          })
                                        }
                                        reachedLimit={reachedLimit}
                                        recipe={offeredRecipe}
                                        removeRecipe={removeRecipeByUuid}
                                        onEditFlexPortions={openFlexPortionEditor}
                                        trackingData={trackingData}
                                      />
                                    ) : (
                                      <SearchRecipeTile
                                        addRecipe={(recipeId) =>
                                          addRecipeByUuid(recipeId, 'menu_search_screen')
                                        }
                                        menuPreferences={menuPreferences}
                                        showCustomisedTileFooter={isAuthenticated}
                                        onClick={() =>
                                          viewRecipeDetails({
                                            invocationId,
                                            menuId: basketMenuUuid,
                                            recipeId: offeredRecipe?.id || null,
                                            recipeRank: index,
                                          })
                                        }
                                        reachedLimit={reachedLimit}
                                        recipe={offeredRecipe}
                                        removeRecipe={removeRecipeByUuid}
                                        trackingData={trackingData}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Visible>
                              <Visible display={[Display.Block, Display.None]}>
                                {offeredRecipe && (
                                  <Box paddingBottom={4}>
                                    <RecipeTile
                                      recipe={offeredRecipe}
                                      addRecipe={(recipeId) => {
                                        queueTrackingEvent<TrackCTAClick>({
                                          action: 'add_recipe',
                                          properties: searchTrackingData,
                                        })
                                        addRecipeByUuid(recipeId, 'menu_search_screen')
                                      }}
                                      alternativeOptions={{ optionsType: 'none', options: [] }}
                                      canAddRecipes={canAddRecipes}
                                      currentCollectionId={currentCollectionId}
                                      menuId={basketMenuUuid}
                                      isInBasket={basketHasRecipe(offeredRecipe.id)}
                                      isOutOfStock={false}
                                      onClick={() =>
                                        viewRecipeDetails({
                                          invocationId,
                                          menuId: basketMenuUuid,
                                          recipeId: offeredRecipe.id,
                                          recipeRank: index,
                                        })
                                      }
                                      originalId={offeredRecipe.id}
                                      reachedLimit={reachedLimit}
                                      recipeReference=""
                                      removeRecipe={(recipeId) => {
                                        queueTrackingEvent<TrackCTAClick>({
                                          action: 'remove_recipe',
                                          properties: searchTrackingData,
                                        })
                                        removeRecipeByUuid(recipeId)
                                      }}
                                      selectVariant={() => {}}
                                      setBrowserCTAVisible={() => {}}
                                      menuPreferences={menuPreferences}
                                      showCustomisedTileFooter={isAuthenticated}
                                      showFlexPortionTileBadge={isFlexRecipesWebEnabled}
                                      insideOverflow
                                      numPortions={recipeNumPortions}
                                      quantity={recipeQuantity}
                                      onUpdateRecipePortions={updateSearchRecipePortions}
                                    />
                                  </Box>
                                )}
                              </Visible>
                            </React.Fragment>
                          )
                        })}
                      </>
                    ) : (
                      <NoResults />
                    )}
                  </Box>
                  {categories.length > 0 && (
                    <Box paddingBottom={4}>
                      <SearchCategoriesGrid
                        categories={categories.map((category) => ({
                          id: category.id as CollectionUUID,
                          slug: category.slug as CollectionSlug,
                          imageUrl: getCategoryImageUrl(category.id),
                          name: category.short_title,
                          recipeCount: category.recipe_count,
                        }))}
                        title="Related Categories"
                      />
                    </Box>
                  )}
                </Box>
              )
            } else if (isLoading) {
              return <RelevantRecipesSkeleton />
            } else {
              return (
                <>
                  <PopularCategories />
                  <Space size={4} />
                  <SuggestedRecipe
                    addRecipe={addRecipeByUuid}
                    canAddRecipes={canAddRecipes}
                    menuPreferences={menuPreferences}
                    onClick={(recommendedRecipeId) =>
                      viewRecipeDetails({
                        menuId: basketMenuUuid,
                        recipeId: recommendedRecipeId,
                      })
                    }
                    isFlexRecipesWebEnabled={isFlexRecipesWebEnabled}
                    reachedLimit={reachedLimit}
                    removeRecipe={removeRecipeByUuid}
                    onEditFlexPortions={openFlexPortionEditor}
                    onUpdateRecipePortions={updateSearchRecipePortions}
                  />
                </>
              )
            }
          })()}
        </Box>
      </Box>
      {isFlexPortionEditorOpen && <FlexModalOverlay onClick={closeFlexPortionEditor} />}
      {flexPortionEditorRecipe && isFlexPortionEditorOpen && (
        <Box
          bottom="0"
          left="0"
          position={DisplayPosition.Absolute}
          right="0"
          width="100%"
          zIndex={ZIndex.Top}
        >
          <ModalFooter>
            <Box className={css.modalFooterContent}>
              <FlexPortionFooter
                recipeCount={flexPortionEditorRecipeCount}
                numPortions={getNumPortionsForRecipeId(flexPortionEditorRecipe.recipe.id)}
                portions={flexPortionEditorRecipe.recipe.portions}
                onUpdatePortions={updateFlexPortionEditorRecipePortions}
                onRemoveRecipe={removeFlexPortionEditorRecipe}
                mode="editor"
                onCloseEditor={closeFlexPortionEditor}
              />
            </Box>
          </ModalFooter>
        </Box>
      )}
    </Modal>
  )
}
